import { promotionSession } from '../utils/promotionSession';
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';

interface UserProfile {
  onboarding_complete?: boolean;
  seen_lifetime_offer?: boolean;
  lifetime_plan?: boolean;
  current_plan_id?: number;
  email_verified?: boolean;
  followed_platforms?: string[];
  id: number;
  email: string;
  profile: {
    firstName?: string;
    lastName?: string;
    [key: string]: any;
  };
}

interface AuthContextType {
  user: UserProfile | null;
  token: string | null;
  loading: boolean;
  profileVerified: boolean;
  authSessionId: string;
  login: (token: string, user: UserProfile) => void;
  replaceToken: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [profileVerified, setProfileVerified] = useState(false);
  const [authSessionId, setAuthSessionId] = useState('');
  const authVersion = useRef(0);

  const login = useCallback((nextToken: string, nextUser: UserProfile) => {
    authVersion.current += 1;
    setAuthSessionId(promotionSession(nextUser.id, true));
    setProfileVerified(true);
    setToken(nextToken);
    setUser(nextUser);
    try {
      localStorage.setItem('token', nextToken);
      localStorage.setItem('user', JSON.stringify(nextUser));
    } catch { /* Storage restrictions must not break the current session. */ }
  }, []);

  // Same user, new credential (e.g. after a password change ends older sessions).
  const replaceToken = useCallback((nextToken: string) => {
    authVersion.current += 1;
    setToken(nextToken);
    try { localStorage.setItem('token', nextToken); } catch { /* Keep the in-memory session usable. */ }
  }, []);

  const logout = useCallback(() => {
    authVersion.current += 1;
    setProfileVerified(false);
    setAuthSessionId('');
    setToken(null);
    setUser(null);
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch { /* Leave manuscripts and other local state intact. */ }
  }, []);

  useEffect(() => {
    let active = true;
    const version = authVersion.current;
    let storedToken: string | null = null;
    try {
      storedToken = localStorage.getItem('token');
      if (storedToken) setToken(storedToken);
      const cached = JSON.parse(localStorage.getItem('user') || 'null');
      if (storedToken) {
        if (cached && Number.isInteger(cached.id)) { setUser(cached); setAuthSessionId(promotionSession(cached.id)); }
      }
    } catch { /* A corrupt profile can be recovered by /me. */ }
    setLoading(false);
    if (storedToken) {
      const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5050/api' : '/api');
      fetch(`${API_BASE_URL}/auth/me`, { headers: { Authorization: `Bearer ${storedToken}` } })
        .then(async res => {
          if (!active || version !== authVersion.current) return;
          if (res.status === 401 || res.status === 404) { logout(); return; }
          if (!res.ok) return; // Offline/server failure is not an expired session.
          const data = await res.json();
          if (!active || version !== authVersion.current || !Number.isInteger(data.user?.id)) return;
          setUser(data.user);
          setAuthSessionId(promotionSession(data.user.id));
          setProfileVerified(true);
          try { localStorage.setItem('user', JSON.stringify(data.user)); } catch {}
        }).catch(() => {});
    }
    return () => { active = false; };
  }, [logout]);

  // A plan change made elsewhere (checkout in another tab, an admin change)
  // used to show only after a hard refresh, because the profile was read once
  // per page load. Re-read it when the tab comes back into view, at most
  // every 30 seconds.
  useEffect(() => {
    let last = Date.now();
    const refresh = () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < 30000) return;
      let storedToken: string | null = null;
      try { storedToken = localStorage.getItem('token'); } catch { return; }
      if (!storedToken) return;
      last = Date.now();
      const version = authVersion.current;
      const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5050/api' : '/api');
      fetch(`${API_BASE_URL}/auth/me`, { headers: { Authorization: `Bearer ${storedToken}` } })
        .then(async res => {
          if (!res.ok || version !== authVersion.current) return;
          const data = await res.json();
          if (version !== authVersion.current || !Number.isInteger(data.user?.id)) return;
          setUser(prev => (prev && JSON.stringify(prev) === JSON.stringify(data.user) ? prev : data.user));
          try { localStorage.setItem('user', JSON.stringify(data.user)); } catch {}
        }).catch(() => {});
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => { document.removeEventListener('visibilitychange', refresh); window.removeEventListener('focus', refresh); };
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, profileVerified, authSessionId, login, replaceToken, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}; 